import { api, queryString, type Items, type Paginated } from '@sentinel/web-core';
import type {
  ConsoleAuditEntry,
  ConsoleModule,
  ConsoleOrganization,
  ModuleAvailability,
  ModuleKey,
  ModuleRequest,
  OrganizationLimits,
  OrganizationProvision,
  PlanKey,
  PlatformAccessGrant,
  PlatformMember,
  PlatformRetention,
  PlatformRoleKey,
  RetentionMode,
  SmtpSettings,
  SmtpSettingsUpdate,
  SmtpTestResult,
} from '@sentinel/shared';

type StatusChange = 'suspend' | 'reactivate' | 'archive';
const org = (id: string) => `/console/organizations/${id}`;

/** The console API (phase F): organizations, modules, requests, team, audit, platform mail, and
 *  the support windows of the platform role. */
export const consoleEndpoints = {
  organizations: () => api<Items<ConsoleOrganization>>('/console/organizations'),
  organization: (id: string) => api<ConsoleOrganization>(org(id)),
  provision: (body: OrganizationProvision) =>
    api<ConsoleOrganization>('/console/organizations', { method: 'POST', body }),
  setPlan: (id: string, planKey: PlanKey) =>
    api<ConsoleOrganization>(`${org(id)}/plan`, { method: 'PUT', body: { planKey } }),
  setLimits: (id: string, body: OrganizationLimits) =>
    api<ConsoleOrganization>(`${org(id)}/limits`, { method: 'PUT', body }),
  changeStatus: (id: string, change: StatusChange) =>
    api<ConsoleOrganization>(`${org(id)}/${change}`, { method: 'POST' }),
  modules: (id: string) =>
    api<{ organizationId: string; items: ConsoleModule[] }>(`${org(id)}/modules`),
  setModule: (id: string, key: ModuleKey, body: ModuleAvailability) =>
    api<{ organizationId: string; items: ConsoleModule[] }>(`${org(id)}/modules/${key}`, {
      method: 'PUT',
      body,
    }),
  moduleRequests: (status: string) =>
    api<Items<ModuleRequest>>(`/console/module-requests${queryString({ status })}`),
  grantRequest: (id: string, body: ModuleAvailability) =>
    api<ModuleRequest>(`/console/module-requests/${id}/grant`, { method: 'POST', body }),
  declineRequest: (id: string) =>
    api<ModuleRequest>(`/console/module-requests/${id}/decline`, { method: 'POST' }),
  members: () => api<Items<PlatformMember>>('/console/members'),
  upsertMember: (email: string, role: PlatformRoleKey) =>
    api<Items<PlatformMember>>('/console/members', { method: 'PUT', body: { email, role } }),
  removeMember: (userId: string) =>
    api<Items<PlatformMember>>(`/console/members/${userId}`, { method: 'DELETE' }),
  audit: (params: { own: boolean; page: number; pageSize: number; action?: string }) =>
    api<Paginated<ConsoleAuditEntry>>(
      `/console/audit${params.own ? '/own' : ''}${queryString({
        page: params.page,
        pageSize: params.pageSize,
        action: params.action,
      })}`,
    ),
  smtp: () => api<SmtpSettings>('/console/smtp'),
  updateSmtp: (body: SmtpSettingsUpdate) =>
    api<SmtpSettings>('/console/smtp', { method: 'PUT', body }),
  testSmtp: (to: string) =>
    api<SmtpTestResult>('/console/smtp/test', { method: 'POST', body: { to } }),
  retention: () => api<PlatformRetention>('/console/retention'),
  setRetentionMode: (mode: RetentionMode) =>
    api<PlatformRetention>('/console/retention', { method: 'PUT', body: { mode } }),
  supportRequests: () => api<Items<PlatformAccessGrant>>('/platform/access-requests'),
  requestSupport: (organizationId: string, reason: string) =>
    api<PlatformAccessGrant>('/platform/access-requests', {
      method: 'POST',
      body: { organizationId, reason },
    }),
  closeSupport: (grantId: string) =>
    api<PlatformAccessGrant>(`/platform/access-requests/${grantId}/close`, { method: 'POST' }),
};
